import React, { useMemo, useState } from "react";
import { fetchLnpPage } from "../../../api/lnp";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import Pagination from "../../../components/ui/Pagination";
import type { ILnp } from "../../../type";
import useBrowseLnp from "../hooks/useBrowseLnp";
import useDebouncedValue from "../hooks/useDebouncedValue";
import { extractApiError } from "../helpers";

interface Props {
  inCartPks: Set<number>;
  onAdd: (row: ILnp) => void;
  onAddMany: (rows: ILnp[]) => void;
  showInfo: (config: {
    title: string;
    message: React.ReactNode;
    variant?: "success" | "info" | "warning";
  }) => void;
  active: boolean;
}

const PAGE_SIZES = [10, 20, 50, 100];

/**
 * Left pane of the Migration tab: a paginated table of beta rows that
 * admins browse and add to a "cart" on the right. Each row knows three
 * flags:
 *   - in cart   → button becomes "In cart ✓", disabled
 *   - in product → shows a gray "prod" badge (already published, adding
 *                  would just be a no-op skip)
 *   - neither   → normal Add button
 *
 * The "Add page" shortcut adds every row on the current page that isn't
 * already in the cart. "Add filter" fetches every page under the current
 * search (capped at MAX_BULK_ADD) and adds them in one go. Both routes
 * through `onAdd` / `onAddMany` which the parent merges into its cart
 * state. This keeps this component stateless with respect to cart.
 */

// Soft cap on bulk-add — matches the backend selection cap (100k) divided
// by a safety factor. Prevents a runaway browser-side request fanout.
const MAX_BULK_ADD = 10000;

const MigrationBrowserPanel: React.FC<Props> = ({
  inCartPks,
  onAdd,
  onAddMany,
  showInfo,
  active,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  const {
    rows,
    count,
    page,
    setPage,
    pageSize,
    setPageSize,
    inProductPks,
    isLoading,
    error,
    reload,
  } = useBrowseLnp(debouncedSearch, active);

  const [isBulkLoading, setIsBulkLoading] = useState(false);
  const [selectedPks, setSelectedPks] = useState<Set<number>>(new Set());

  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  const addablePageRows = useMemo(
    () => rows.filter((r) => !inCartPks.has(r.index)),
    [rows, inCartPks]
  );

  const handleAddSelected = () => {
    if (selectedPks.size === 0) return;
    const selectedRows = rows.filter((r) => selectedPks.has(r.index) && !inCartPks.has(r.index));
    if (selectedRows.length > 0) {
      onAddMany(selectedRows);
    }
    setSelectedPks(new Set());
  };

  const handleAddAllMatching = async () => {
    if (count === 0 || isBulkLoading) return;
    setIsBulkLoading(true);
    try {
      // Fetch every page under the current filter. We page through with
      // the same endpoint so server-side filtering behavior stays identical.
      const collected: ILnp[] = [];
      const perPage = 200;
      const pageCount = Math.ceil(count / perPage);
      for (let p = 1; p <= pageCount; p++) {
        const batch = await fetchLnpPage({
          page: p,
          pageSize: perPage,
          search: debouncedSearch || undefined,
          ordering: "index",
          status: "draft",
        });
        batch.results.forEach((r) => {
          if (!inCartPks.has(r.index)) collected.push(r);
        });
      }
      onAddMany(collected);
      showInfo({
        title: "Added to cart",
        message: `${collected.length.toLocaleString()} row(s) added. ${
          count - collected.length
        } already in cart (skipped).`,
        variant: "success",
      });
    } catch (err) {
      showInfo({
        title: "Add failed",
        message: extractApiError(err, "Bulk add failed."),
        variant: "warning",
      });
    } finally {
      setIsBulkLoading(false);
    }
  };

  return (
    <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">Browse beta data</h3>
        <span className="text-xs text-gray-500">
          {count.toLocaleString()} row{count === 1 ? "" : "s"} match
        </span>
      </div>

      {/* Top toolbar — search + controls on the left, bulk-add actions on
          the right. Keeping Add beside the filter that defines "matching"
          reduces mental distance between narrowing the set and acting on
          it. Flex-wrap handles narrow screens by stacking groups. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search il_name, experiment_id, hl_name..."
          className="flex-1 min-w-[12rem] p-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="p-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          aria-label="Page size"
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-50"
        >
          {isLoading && <LoadingSpinner variant="inline" />}
          Refresh
        </button>
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            disabled={selectedPks.size === 0}
            onClick={handleAddSelected}
            className="px-3 py-1.5 text-xs text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50 disabled:opacity-40"
          >
            Add selected ({selectedPks.size})
          </button>
          <button
            type="button"
            disabled={count === 0 || isBulkLoading}
            onClick={handleAddAllMatching}
            className="px-3 py-1.5 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {isBulkLoading
              ? "Adding..."
              : `Add all matching (${count.toLocaleString()})`}
          </button>
        </div>
      </div>

      {error && (
        <div
          className="p-3 mb-3 text-sm text-red-800 border border-red-200 rounded-lg bg-red-50"
          role="alert"
        >
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        {/* Every load shows the block spinner — the table only appears
            once fresh rows are ready, not while stale rows would sit on
            screen during pagination. Debounced search + seq-ref stale
            guards in useBrowseLnp keep this from flickering per keystroke. */}
        {isLoading ? (
          <LoadingSpinner label="Loading beta rows" />
        ) : (
        <table className="w-full text-sm text-left">
          <thead className="text-xs text-gray-500 uppercase bg-gray-50">
            <tr>
              <th className="px-2 py-1.5 w-10">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selectedPks.size === rows.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPks(new Set(rows.map(r => r.index)));
                    } else {
                      setSelectedPks(new Set());
                    }
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </th>
              <th className="px-2 py-1.5">index</th>
              <th className="px-2 py-1.5">experiment_id</th>
              <th className="px-2 py-1.5">il_name</th>
              <th className="px-2 py-1.5">cargo</th>
              <th className="px-2 py-1.5">status</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 py-6 text-center text-gray-400">
                  No rows match.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const inCart = inCartPks.has(row.index);
              const inProd = inProductPks.has(row.index);
              const renderKey = row.index ?? `null-${(row as any).lnp_id ?? Math.random()}`;
              return (
                <tr
                  key={renderKey}
                  className={`border-t border-gray-100 hover:bg-gray-50 ${
                    inCart ? "bg-blue-50/50" : ""
                  }`}
                >
                  <td className="px-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedPks.has(row.index)}
                      disabled={inCart}
                      onChange={(e) => {
                        const newSet = new Set(selectedPks);
                        if (e.target.checked) newSet.add(row.index);
                        else newSet.delete(row.index);
                        setSelectedPks(newSet);
                      }}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                    />
                  </td>
                  <td className="px-2 py-1 text-gray-900 tabular-nums">
                    {row.index}
                  </td>
                  <td className="px-2 py-1 text-gray-700">
                    {row.experiment_id ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-gray-700">
                    {row.il_name ?? "—"}
                  </td>
                  <td className="px-2 py-1 text-gray-700">
                    {row.cargo ?? "—"}
                  </td>
                  <td className="px-2 py-1">
                    {inProd && (
                      <span
                        className="inline-flex items-center px-1.5 py-0 text-[10px] font-medium rounded bg-gray-200 text-gray-600"
                        title="Already published to product"
                      >
                        prod
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>

      {/* Bottom: centered pagination. Wrapping in flex justify-center
          centers the control regardless of panel width. */}
      <div className="flex justify-center pt-3 mt-3 border-t border-gray-100">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={count}
          unit="rows"
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default MigrationBrowserPanel;
